import React, { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Voting from "../components/Voting";
import NotFound from "../components/NotFound";
import { usePollBySlug } from "@core/hooks/usePoll";
import { logger } from "@core/lib/logger";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { SEOHead, detectPollCategory } from "@core/components/SEOHead";
import { PromoBanner } from "../components/PromoBanner";
import { PromoSlot } from "../components/PromoSlot";
import { PollTemplateHeader } from "../components/PollTemplateHeader";
import { ReportPollLink } from "../components/ReportPollDialog";
import FinalDateCard from "@core/components/final-date/FinalDateCard";
import { readFinalDate } from "@core/lib/final-date";

/**
 * Page de vote pour les sondages de dates
 */
export default function Vote() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation(["polls", "common", "errors"]);
  const [voteSubmitted, setVoteSubmitted] = useState(false);

  // Utiliser le hook unifié avec le slug
  const { data: poll, isLoading, error } = usePollBySlug(slug);

  if (isLoading) {
    return (
      <div className="flex min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <ProductSidebar productType="date" />
        <div className="flex-1">
          <ProductHeader productType="date" showAuth={true} />
          <div className="flex items-center justify-center flex-1">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !poll) {
    return (
      <NotFound
        title={
          error ? t("errors:poll_load_title") : t("polls:poll_not_found_title")
        }
        message={
          error
            ? t("errors:poll_load_message")
            : t("polls:poll_not_found_message")
        }
        hint={slug ? t("polls:poll_slug_hint", { slug }) : undefined}
        testId="poll-not-found"
      />
    );
  }

  // Rediriger vers le viewer form-polls si c'est un formulaire
  const pollType = (poll as any)?.kind || (poll as any)?.type || "date";
  if (pollType === "form") {
    return <Navigate to={`/form/poll/${slug}/vote`} replace />;
  }

  // Vérifier le statut du sondage avant d'autoriser le vote.
  // Bug-hunt Nuit #18 : la page ne bloquait que le statut "draft", donc
  // un sondage archivé ou terminé restait votable (`/vote` rendait la grille
  // et le bouton "Envoyer mes votes"). On lit `poll.status` qui est garanti
  // venir du top-level `polls.status` (cf. PollService.getPollBySlug → status: data.status || pollData.status).
  //
  // Bug-hunt Nuit #19 : la clé "completed" ne correspondait à AUCUNE valeur du
  // CHECK constraint DB `polls_status_check` (draft | active | closed | archived).
  // L'action kebab "Terminer le sondage" écrit `status = "closed"`, donc
  // `copy["closed"]` était `undefined` → `c.testId` crashait `/vote`
  // (`Cannot read properties of undefined (reading 'testId')`). On aligne la clé
  // sur la valeur DB (`closed`) et on ajoute un fallback défensif pour tout
  // statut non bloquant inconnu (au lieu de crasher).
  const rawStatus = (poll as { status?: string }).status ?? "active";
  const blockingStatus = rawStatus as
    | "draft"
    | "archived"
    | "closed"
    | "active";

  const blockingCopy: Record<
    "draft" | "archived" | "closed",
    { title: string; message: string; hint: string; testId: string }
  > = {
    draft: {
      title: t("polls:poll_status_draft_title"),
      message: t("polls:poll_status_draft_message"),
      hint: t("polls:poll_status_draft_hint"),
      testId: "poll-status-draft",
    },
    archived: {
      title: t("polls:poll_status_archived_title"),
      message: t("polls:poll_status_archived_message"),
      hint: t("polls:poll_status_archived_hint"),
      testId: "poll-status-archived",
    },
    closed: {
      title: t("polls:poll_status_closed_title"),
      message: t("polls:poll_status_closed_message"),
      hint: t("polls:poll_status_closed_hint"),
      testId: "poll-status-closed",
    },
  };

  const c =
    blockingStatus !== "active"
      ? (blockingCopy[blockingStatus as "draft" | "archived" | "closed"] ??
        blockingCopy.closed)
      : undefined;

  // Sondage clos AVEC date fixée (spec S3) : « Date retenue » au lieu de
  // l'alerte « sondage terminé », avec ajout à l'agenda et annonce.
  const finalDate = blockingStatus === "closed" ? readFinalDate(poll) : null;
  if (finalDate) {
    return (
      <div className="flex min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <ProductSidebar productType="date" />
        <div className="flex-1 min-w-0">
          <ProductHeader productType="date" showAuth={true} />
          <main
            className="max-w-xl mx-auto px-4 pt-24 pb-10 space-y-4"
            data-testid="poll-final-date"
          >
            {poll.title && (
              <h1 className="text-center text-lg font-semibold text-foreground break-words">
                {poll.title}
              </h1>
            )}
            <FinalDateCard
              poll={{ id: poll.id, title: poll.title, slug }}
              finalDate={finalDate}
              variant="voter"
            />
            {slug && (
              <div className="text-center">
                <a
                  href={`/poll/${slug}/results`}
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                >
                  {t("polls:view_results")}
                </a>
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  if (c) {
    return (
      <div className="flex min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <ProductSidebar productType="date" />
        <div className="flex-1">
          <ProductHeader productType="date" showAuth={true} />
          <div
            className="flex items-center justify-center min-h-screen-safe bg-background dark:bg-[#0a0a0a]"
            data-testid={c.testId}
          >
            <div className="text-center max-w-md mx-auto p-6">
              <div className="mb-4">
                <div className="w-16 h-16 bg-warning-bg rounded-full flex items-center justify-center mx-auto">
                  <svg
                    className="w-8 h-8 text-warning"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {c.title}
              </h2>
              <p className="text-muted-foreground mb-4">
                {c.message}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {c.hint}
              </p>
              <div className="space-y-2">
                {blockingStatus !== "draft" && slug && (
                  <button
                    onClick={() =>
                      (window.location.href = `/poll/${slug}/results`)
                    }
                    className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
                  >
                    {t("polls:view_results")}
                  </button>
                )}
                <button
                  onClick={() => window.history.back()}
                  className="w-full px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:opacity-90 transition-opacity"
                >
                  {t("common:back")}
                </button>
                <button
                  onClick={() => (window.location.href = "/dashboard")}
                  className="w-full px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                >
                  {t("polls:go_to_dashboard")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Prepare data for SEO
  const seoData = {
    id: poll.id,
    slug,
    title: poll.title,
    type: "date-poll",
    participantCount: (poll as any).responses?.length || 0,
    deadline: (poll as any).deadline,
    dates: (poll as any).dates || [],
    category: detectPollCategory(poll.title || "Sondage"), // i18n-exempt: fallback non affiché, sert seulement à la détection de catégorie
  };

  const template = poll.template ?? null;

  return (
    <>
      <SEOHead poll={seoData} />
      <div className="flex flex-col min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <ProductHeader productType="date" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="date" />
          <div
            className="flex-1 flex flex-col"
            style={
              template
                ? { backgroundColor: `${template.secondary_color}1a` }
                : undefined
            }
          >
            {template && (
              <PollTemplateHeader template={template} title={poll.title} />
            )}
            <div className="flex-1">
              <Voting
                pollId={poll.id}
                pollType="date"
                slug={slug}
                onVoteSubmitted={() => {
                  logger.info("Vote submitted", "vote");
                  setVoteSubmitted(true);
                }}
              />
            </div>
            {/*
              « Signaler ce sondage » (spec IA §3.7) : AVANT l'encart promo,
              qui est collé en bas d'écran sur mobile après le vote (#1354)
              et porte seul la marge de la zone sûre.
            */}
            <ReportPollLink
              pollId={poll.id}
              pollSlug={slug}
              pollTitle={poll.title}
            />
            {/*
              L'encart d'essai ne remplace la bannière générique qu'APRÈS
              l'enregistrement du vote. Cette page a un seul travail — faire
              voter — et le taux de réponse est la valeur que le créateur
              achète : tout ce qui s'interpose avant le vote se paie dessus.
            */}
            {voteSubmitted ? (
              // Collé en bas sur mobile, comme sur Results : sinon l'offre
              // n'apparaît qu'après scroll. Seulement après le vote, pour ne
              // pas occuper l'écran pendant qu'on vote.
              <div className="sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)] bg-background md:static md:pb-0">
                <PromoSlot placement="vote_page" />
              </div>
            ) : (
              <PromoBanner />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
